/**
 * Design-system tab primitives.
 * `Tabs` — underline style (project / claim detail).
 * `Segmented` — pill toggle (calendar/timesheets/person/persona).
 */
import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface Tab {
  key: string;
  label: string;
}

export function Tabs({
  tabs,
  active,
  onSelect,
  className,
}: {
  tabs: Tab[];
  active: string;
  onSelect: (key: string) => void;
  className?: string;
}) {
  return (
    <div className={cn('flex gap-1 border-b border-hairline', className)}>
      {tabs.map((t) => {
        const on = t.key === active;
        return (
          <button
            key={t.key}
            onClick={() => onSelect(t.key)}
            className={cn(
              'relative px-[15px] py-[11px] text-[13.5px] font-medium font-sans',
              on ? 'text-ink-strong' : 'text-dim'
            )}
          >
            {t.label}
            {on && (
              <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-sm bg-primary" />
            )}
          </button>
        );
      })}
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: { value: T; label: string; icon?: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
}) {
  return (
    <div className={cn('flex gap-[3px] rounded-[9px] bg-muted p-[3px]', className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={cn(
              'flex items-center gap-1.5 rounded-[7px] px-3 py-1.5 text-[12.5px] font-medium',
              on ? 'bg-surface text-ink shadow-[var(--shadow-card)]' : 'bg-transparent text-dim'
            )}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
